<script lang="ts">
	import { onMount } from 'svelte';
	import { browser } from '$app/environment';
	import * as Dialog from '$lib/components/ui/dialog';
	import * as Drawer from '$lib/components/ui/drawer';
	import { Button } from '$lib/components/ui/button';
	import { ArrowRight, Percent } from 'lucide-svelte';
	import TasIcon from '$lib/assets/icons/TasIcon.svelte';

	let open = $state(false);
	let shouldShow = $state(false);
	let isDesktop = $state(true);

	// Session storage key for tracking if dialog has been dismissed
	const DISMISSED_KEY = 'multiTourDiscountDismissed';

	onMount(() => {
		// Set up media query listener
		const mediaQuery = window.matchMedia('(min-width: 768px)');
		isDesktop = mediaQuery.matches;

		const handleMediaChange = (e: MediaQueryListEvent) => {
			isDesktop = e.matches;
		};

		mediaQuery.addEventListener('change', handleMediaChange);

		// Check if user has already dismissed the dialog in this session
		const dismissed = sessionStorage.getItem(DISMISSED_KEY);

		if (!dismissed) {
			// Show dialog after a short delay (e.g., 2 seconds)
			const timer = setTimeout(() => {
				shouldShow = true;
				open = true;
			}, 2000);

			return () => {
				clearTimeout(timer);
				mediaQuery.removeEventListener('change', handleMediaChange);
			};
		}

		return () => {
			mediaQuery.removeEventListener('change', handleMediaChange);
		};
	});

	function handleOpenChange(isOpen: boolean) {
		open = isOpen;

		// If dialog is being closed, mark as dismissed for this session
		if (!isOpen && browser) {
			sessionStorage.setItem(DISMISSED_KEY, 'true');
		}
	}

	function handleExploreClick() {
		open = false;
		if (browser) {
			sessionStorage.setItem(DISMISSED_KEY, 'true');
		}
	}
</script>

{#if shouldShow}
	{#if isDesktop}
		<Dialog.Root bind:open onOpenChange={handleOpenChange}>
			<Dialog.Content class="max-w-2xl border-none bg-transparent p-0 shadow-2xl sm:rounded-2xl">
				<div
					class="group relative flex min-h-[500px] w-full flex-col items-center justify-center overflow-hidden rounded-2xl"
				>
					<!-- Background Image -->
					<enhanced:img
						src="$lib/assets/images/toursaroundtasmania.jpg"
						alt="Book multiple day tours and save"
						class="absolute inset-0 h-full w-full object-cover"
					/>

					<!-- Gradient Overlay -->
					<div
						class="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/40 to-foreground/20"
					></div>

					<!-- Content -->
					<div class="relative z-10 flex flex-col items-center justify-center px-8 text-center">
						<!-- Icon Badge with Tasmania Shape -->
						<!-- <div class="relative mb-6 flex h-20 w-20 items-center justify-center">
							<TasIcon class="shadow-lg" />
							<div class="absolute inset-0 flex items-center justify-center">
								<Percent class="h-8 w-8 stroke-foreground" />
							</div>
						</div> -->

						<!-- Heading -->
						<h2
							class="mb-4 font-heading text-3xl font-bold tracking-tight text-background lg:text-4xl"
						>
							Save <span class="text-[#ffd451]">15%</span> on Multiple Tours
						</h2>

						<!-- Description -->
						<p class="mb-6 max-w-md text-lg text-background/90 lg:text-xl">
							Book 2 or more day tours and receive <span class="font-black">15% OFF</span> your entire
							order
						</p>

						<!-- CTA Buttons -->
						<div class="flex flex-col gap-3">
							<a href="/tours#dayTours" onclick={handleExploreClick}>
								<Button
									class="inline-flex items-center gap-2 rounded-full bg-tt-red px-6 py-3 font-semibold text-background transition-all hover:bg-tt-red/90"
								>
									Explore Day Tours
									<ArrowRight class="h-5 w-5 transition-transform group-hover:translate-x-1" />
								</Button>
							</a>
							<Dialog.Close>
								<Button variant="link" class="text-background underline hover:text-background/80">
									Maybe Later
								</Button>
							</Dialog.Close>
						</div>
					</div>
				</div>
			</Dialog.Content>
		</Dialog.Root>
	{:else}
		<Drawer.Root bind:open onOpenChange={handleOpenChange}>
			<Drawer.Content class="overflow-hidden rounded-t-[10px] border-none bg-transparent p-0">
				<!-- Background Image - covers entire drawer -->
				<enhanced:img
					src="$lib/assets/images/toursaroundtasmania.jpg"
					alt="Book multiple day tours and save"
					class="absolute inset-0 h-full w-full rounded-t-[10px] object-cover"
				/>

				<!-- Gradient Overlay -->
				<div
					class="absolute inset-0 rounded-t-[10px] bg-gradient-to-t from-foreground/80 via-foreground/40 to-foreground/20"
				></div>

				<!-- Drawer Handle (keep visible) -->
				<div class="relative z-10 mx-auto mt-4 h-2 w-[100px] rounded-full bg-background/40"></div>

				<!-- Content Area -->
				<div
					class="relative z-10 flex min-h-[500px] flex-col items-center justify-center px-8 py-8 text-center"
				>
					<!-- Icon Badge with Tasmania Shape -->
					<div class="relative mb-6 flex h-20 w-20 items-center justify-center">
						<TasIcon class="shadow-lg" />
						<div class="absolute inset-0 flex items-center justify-center">
							<Percent class="h-8 w-8 stroke-foreground" />
						</div>
					</div>

					<!-- Heading -->
					<Drawer.Title class="mb-4 font-heading text-3xl font-bold tracking-tight text-background">
						Save 15% on Multiple Tours
					</Drawer.Title>

					<!-- Description -->
					<Drawer.Description class="mb-6 max-w-md text-lg text-background/90">
						Book 2 or more day tours and receive <span class="font-black">15% OFF</span> your entire
						order
					</Drawer.Description>

					<!-- CTA Button -->
					<a href="/tours#dayTours" onclick={handleExploreClick}>
						<Button
							class="inline-flex items-center gap-2 rounded-full bg-tt-red px-6 py-3 font-semibold text-background transition-all hover:bg-tt-red/90"
						>
							Explore Day Tours
							<ArrowRight class="h-5 w-5 transition-transform group-hover:translate-x-1" />
						</Button>
					</a>
				</div>

				<!-- Footer with Maybe Later button -->
				<div class="relative z-10 flex justify-center px-4 pb-8">
					<Drawer.Close>
						<Button variant="link" class="text-background underline hover:text-background/80">
							Maybe Later
						</Button>
					</Drawer.Close>
				</div>
			</Drawer.Content>
		</Drawer.Root>
	{/if}
{/if}
